import React, { useEffect, useState } from 'react';
import { Grid, MenuItem, TextField, Box, Button, IconButton, Checkbox } from '@mui/material';
import { getSingleOrgSeasonApi } from '../../api/orgSeasonApi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { OrgSeason } from '../../api/models/orgSeason';
import { useAuth0 } from '@auth0/auth0-react';
import { WebCamCapture } from '../../shared/webCamCapture';
import { CircularProgress } from '@mui/material';
import { Team } from '../../api/models/team';
import { ErrorHeader } from '../../shared/errorHeader';
import { PersonView } from '../../models/personView';
import { getMemberProfileImage, updateMemberProfileImage } from '../../api/memberApi';

import { BasicBackButton } from '../../shared/basicBackButton';
import { groupBy } from '../../utils/common';
import { modifyImageApi } from '../../api/imageApi';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';

import AddIcon from '@mui/icons-material/Add';
import { generateQRCode } from '../../utils/qrCodeHelper';
import PreviewIcon from '@mui/icons-material/Preview';
import { useAppUser } from '../../providers/appUserContext';

enum PageState {
	Reset = 'reset',
	PersonSelected = 'personSelected',
	Modifying = 'modifying',
	Update = 'update',
	Save = 'save',
	Saving = 'saving',
	Saved = 'saved',
}

export const ProfileCapturePage: React.FC = (): JSX.Element => {
	const [orgSeason, setOrgSeason] = useState<OrgSeason | undefined>(undefined);
	const [divisions, setDivisions] = useState<Map<string, Team[]> | undefined>(undefined);
	const [selectedDivision, setSelectedDivision] = useState<string>('');
	const [selectedTeam, setSelectedTeam] = useState<Team | undefined>(undefined);
	const [filteredPersonList, setFilteredPersonList] = useState<PersonView[]>([]);
	const [selectedPerson, setSelectedPerson] = React.useState<PersonView | undefined>(undefined);

	const [existingImage, setExistingImage] = useState<string>('');
	const [newImage, setNewImage] = useState<string | undefined>(undefined);
	const [pageState, setPageState] = useState<PageState>(PageState.Reset);

	const [qrCode, setQrCode] = useState<string | undefined>(undefined);

	const [isAutoCrop, setIsAutoCrop] = React.useState(false);
	const [isScrubBkg, setIsScrubBkg] = React.useState(false);

	const [isLoading, setIsLoading] = React.useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

	const [searchParams] = useSearchParams();
	const teamGuid = searchParams.get('team-guid');

	const params = useParams();
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth0();
    const { appUser, getPermission, isAdmin, refreshAppUser } = useAppUser();

	const getOrgSeasonApiRequest = useApiCallWithAuth(getSingleOrgSeasonApi);
	const getMemberProfileImageRequest = useApiCallWithAuth(getMemberProfileImage);
	const modifyImageApiRequest = useApiCallWithAuth(modifyImageApi);
	const updateMemberProfileImageRequest = useApiCallWithAuth(updateMemberProfileImage);

	function getAppRoot(): string {
		const { protocol, host } = window.location;
		return `${protocol}//${host}`;
	}

	useEffect(() => {
		if (!params.orgSeasonGuid) {
			throw Error('OrgSeason Guid is missing');
		}

        console.log(`isAuthenticated: ${isAuthenticated}, isAdmin: ${isAdmin(params.orgSeasonGuid)}`);

		if (!isAuthenticated || !isAdmin(params.orgSeasonGuid)) {
			setErrorMessage('You do not have permission to access this page X');
			return; 
		}

		initPage();
	}, [isAuthenticated, isAdmin, params.orgSeasonGuid]);

	async function initPage() {
		setIsLoading(true);
		setErrorMessage('');

		try {
			const fetchOrgSeason = (await getOrgSeasonApiRequest.request(params.orgSeasonGuid)) as OrgSeason;
			if (fetchOrgSeason) {
				setOrgSeason(fetchOrgSeason);
				const teamList = fetchOrgSeason.teams;
				const divisionList = groupBy<string, Team>(teamList, (team) => team.division);
				setDivisions(divisionList);

				if (teamGuid) {
					const initTeam = teamList.find((team) => team.id === teamGuid);
					if (initTeam) {
						const initDivision = teamList.find((team) => team.division === initTeam.division)?.division;
						if (initDivision) {
							setSelectedDivision(initDivision);
							setSelectedTeam(initTeam);
							const personList = generatePersonList(initTeam);
							setFilteredPersonList(personList);
						}
					}
				}
			} else {
				setErrorMessage(`orgSeason ${params.orgSeasonGuid} not found`);
			}
		} catch (error: any) {
			setErrorMessage(error.message);
		}

		setIsLoading(false);
	}

	const selectDivision = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedDivision(event.target.value);
		setPageState(PageState.Reset);
		setSelectedTeam(undefined);
		setSelectedPerson(undefined);
		setFilteredPersonList([]);
		setNewImage(undefined);
	};

	const selectTeam = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedTeamId = event.target.value;
		const selectedTeam = divisions?.get(selectedDivision)?.find((team) => team.id === selectedTeamId);
		if (selectedTeam) {
			setPageState(PageState.Reset);
			setSelectedTeam(selectedTeam);
			setSelectedPerson(undefined);
			const personList = generatePersonList(selectedTeam);
			setFilteredPersonList(personList);
			setNewImage(undefined);
		} else {
			setErrorMessage('could not find the selected team!');
		}
	};

	function generatePersonList(team: Team): PersonView[] {
		let personList: PersonView[] = [];

		if (team.athletes) {
			const sortedMembers = team.athletes.sort((a, b) => a.lastName.localeCompare(b.lastName));
			const memberPersons = sortedMembers.map((member) => ({
				guid: member.id,
				isLeader: false,
				displayName: `${member.lastName}, ${member.firstName}`,
				profileLink: member.profileLink,
				secCode: member.secCode,
			}));
			personList = personList.concat(memberPersons);
		}

		if (team.leaders) {
			const sortedLeaders = team.leaders.sort((a, b) => a.lastName.localeCompare(b.lastName));
			const leaderPersons = sortedLeaders.map((leader) => ({
				guid: leader.id,
				isLeader: true,
				displayName: `${leader.lastName}, ${leader.firstName} - ${leader.title}`,
				profileLink: leader.profileLink,
				secCode: leader.secCode,
			}));
			personList = personList.concat(leaderPersons);
		}

		return personList;
	}

	async function selectPerson(event: React.ChangeEvent<HTMLInputElement>) {
		const selectedPersonGuid = event.target.value;
		const selectedPerson = filteredPersonList.find((person) => person.guid === selectedPersonGuid);

		if (selectedPerson) {
			setPageState(PageState.PersonSelected);
			setSelectedPerson(selectedPerson);
            setNewImage(undefined);
            setExistingImage('');

			if (selectedPerson.secCode) {
				setQrCode(await generateQRCode(`${getAppRoot()}/card-data/code/${selectedPerson.secCode}`));
			}

			setIsLoading(true);
			setErrorMessage('');

			try {
				const profileImage = await getMemberProfileImageRequest.request(selectedPerson.guid);

				if (profileImage && profileImage.image) {
					setExistingImage(profileImage.image);
				} else {
					setErrorMessage('No profile image found for the selected person');
				}
			} catch (error: any) {
				setErrorMessage(error.message);
			}

			setIsLoading(false);
		} else {
			setErrorMessage('could not find the selected person!');
		}
	}

    function navigateToTeam() {
		if (orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/team/${selectedTeam.id}?source=photo-capture`);
		} else {
			setErrorMessage('OrgSeason or selected team not found');
		}
	}

	function navigateToAddMember() {
		if (orgSeason && selectedTeam) {
			navigate(`/orgseason/${orgSeason.id}/team/${selectedTeam.id}/add?source=photo-capture`);
		} else {
			setErrorMessage('OrgSeason or selected team not found');
		}
	}

	function cancelUpdate() {
		setPageState(PageState.PersonSelected);
	}

	function updateImage() {
		setPageState(PageState.Update);
	}

	//This is what comes from the webcam capture
	async function captureImage(photo: string) {
		setNewImage(photo);

		if (isAutoCrop || isScrubBkg) {
			const modifyImageCommand = {
				cropToHeadshot: isAutoCrop,
				replaceBackground: false,
				removeBackground: isScrubBkg,
				image: photo,
			};

            setIsLoading(true);
			setErrorMessage('');

			try {
				const modifiedImage = await modifyImageApiRequest.request(modifyImageCommand);
				if (modifiedImage) {
					setNewImage(modifiedImage.image);
				} else {
					setErrorMessage('There was an error modifying the image');
				}
			} catch (error: any) {
				setErrorMessage('Error modifying image - original will be used');
			}

            setIsLoading(false);
		}

		setPageState(PageState.Save);
	}

	async function uploadImage() {

        setIsLoading(true);
        setErrorMessage('');

		try {
			setPageState(PageState.Saving);
			if (orgSeason && selectedPerson && newImage) {
				const addImageCommand = { image: newImage };
				const profileImage = await updateMemberProfileImageRequest.request(selectedPerson.guid, addImageCommand);

				if (!profileImage || !profileImage.image) {
					throw new Error('Failed to update profile image');
				}

				setExistingImage(profileImage.image);
				setPageState(PageState.Saved);
			} else {
				setErrorMessage('Either the orgSeason wasnt found, person isnt selected, or image doesnt exist');
				setPageState(PageState.PersonSelected);
			}
		} catch (error: any) {
			setErrorMessage(error.message);
			setPageState(PageState.PersonSelected);
		}

        setIsLoading(false);
	}

	function isState(state: PageState): boolean {
		return state === pageState;
	}

	const renderImageBox = () => {
		return (
			<Box>
				{(isState(PageState.PersonSelected) || isState(PageState.Saved)) && (
					<Grid item xs>
						<Box>
							<Grid container spacing={1} direction="column">
								{isState(PageState.PersonSelected) && (
									<Grid item>
										<label style={{ color: '#000000', fontWeight: '600' }}>Current Image</label>
									</Grid>
								)}

								{isState(PageState.Saved) && (
									<Grid item>
										<label style={{ color: '#008000', fontWeight: '600' }}>Saved</label>
									</Grid>
								)}

                                {existingImage &&
								<Grid item>
									<img src={existingImage} height={300} alt="current profile" />
								</Grid>}

								<Grid item container spacing={1}>
									<Button color="primary" variant="contained" onClick={(e) => updateImage()}>
										Update
									</Button>
								</Grid>
                                

							</Grid>
						</Box>
					</Grid>
				)}

				{isState(PageState.Update) && (
					<Grid item xs>
						<Box>
							<label style={{ color: '#000000', fontWeight: '600' }}>Capture Image</label>
							{isAuthenticated && <WebCamCapture onCapture={captureImage} 
                                                                onCancel={cancelUpdate} 
                                                                size={300} 
                                                                viewPortPctSize={0.75} 
                                                                imageDimension={1000} />}
						</Box>
					</Grid>
				)}

				{isState(PageState.Save) && (
					<Grid item xs>
						<Box>
							<Grid container spacing={1} direction="column">
								<Grid item>
									<label style={{ color: '#000000', fontWeight: '600' }}>Save Image</label>
								</Grid>
								<Grid item>
									<img src={newImage} height={200} alt="profile to save" />
								</Grid>
								<Grid item container spacing={1}>
									<Grid item>
										<Button color="primary" variant="contained" onClick={(e) => uploadImage()}>
											Save
										</Button>
									</Grid>

									<Grid item>
										<Button color="primary" variant="contained" onClick={(e) => cancelUpdate()}>
											Cancel
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					</Grid>
				)}

				{isState(PageState.Modifying || PageState.Saving) && (
					<Grid item xs>
						<Box>
							<CircularProgress />
						</Box>
					</Grid>
				)}
			</Box>
		);
	};

	const renderQrCodeBox = () => {
		return <Box>{qrCode && <img src={qrCode} height={150} alt="current profile" />}</Box>;
	};

	function displayMemberSelector() {
		return (
			<Box>
				<Grid item container spacing={2} direction="column">
					<Grid item>
						<TextField
							select
							fullWidth
							label="Select "
							size="small"
							value={selectedDivision}
							onChange={selectDivision}
							helperText="Select a division"
						>
							{orgSeason &&
								orgSeason.divisions.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
						</TextField>
					</Grid>

					<Grid item>
                    <Box display="flex" alignItems="flex-start" justifyContent="center" gap={1}>
						<TextField
							select
							fullWidth
							label="Select"
							size="small"
							value={selectedTeam?.id ?? ''}
							onChange={selectTeam}
							helperText="Select a team"
						>
							{divisions &&
								divisions.get(selectedDivision)?.map((option) => (
									<MenuItem key={option.id} value={option.id}>
										{option.name}
									</MenuItem>
								))}
						</TextField>
                        {selectedTeam && (
								<IconButton
									onClick={navigateToTeam}
									title="View Team"
									sx={{
										backgroundColor: '#e3e3e3', // light gray background
										'&:hover': {
											backgroundColor: '#bdbdbd', // darker on hover
										},
										border: '1px solid #bdbdbd', // optional: subtle border
									}}
								>
									<PreviewIcon />
								</IconButton>
							)}
						</Box>
					</Grid>

					<Grid item>
						<Box display="flex" alignItems="flex-start" justifyContent="center" gap={1}>
							<TextField
								select
								label="Select"
								size="small"
								value={selectedPerson?.guid ?? ''}
								onChange={selectPerson}
								helperText="Select a member"
								sx={{ flex: 1 }}
							>
								{filteredPersonList.map((option) => (
									<MenuItem key={option.guid} value={option.guid}>
										{option.displayName}
									</MenuItem>
								))}
							</TextField>
							{selectedTeam && (
								<IconButton
									onClick={navigateToAddMember}
									title="Add Member"
									sx={{
										backgroundColor: '#e3e3e3', // light gray background
										'&:hover': {
											backgroundColor: '#bdbdbd', // darker on hover
										},
										border: '1px solid #bdbdbd', // optional: subtle border
									}}
								>
									<AddIcon />
								</IconButton>
							)}
						</Box>
					</Grid>
				</Grid>
			</Box>
		);
	}

	function mainContent() {
		return (
			<>
				<br />
				{displayMemberSelector()}

				<Grid item container spacing={2} direction="row" alignItems="center">
					<Grid item>
						<label>
							<Checkbox checked={isAutoCrop} onChange={(e) => setIsAutoCrop(e.target.checked)} />
							Auto-Crop
						</label>
					</Grid>
					<Grid item>
						<label>
							<Checkbox checked={isScrubBkg} onChange={(e) => setIsScrubBkg(e.target.checked)} />
							Scrub Bkg
						</label>
					</Grid>
				</Grid>

				<Grid item container spacing={2} direction="column">
					<Grid item>
						<Box display="flex" gap={4} alignItems="flex-start">
							{renderImageBox()}
						</Box>
					</Grid>
				</Grid>

                <Grid item container spacing={2} direction="column">
					<Grid item>
						<Box display="flex" gap={4} alignItems="flex-start">
							{renderQrCodeBox()}
						</Box>
					</Grid>
				</Grid>
			</>
		);
	}

	return (
		<Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="100vh" px={2}>
			<Box width="100%" maxWidth="400px">
				<ErrorHeader errorMessage={errorMessage} />
				{orgSeason && <BasicBackButton labelText="" navigateUrl={`/orgseason/${orgSeason.id}`} />}
				{orgSeason && mainContent()}
				{isLoading && <CircularProgress />}
			</Box>
		</Box>
	);
};
